import React, { FC, Suspense, useEffect } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { graphql } from 'relay-runtime';
import { toGlobalID, toLocalID } from 'src/shared/helpers';
import styled from 'styled-components';
import { ZakekeLoading } from '../../widgets/spinner';
import { Center } from '../order/DesignMainOutputFiles';
import { aiStudioDialogModelByCodeQuery } from './__generated__/aiStudioDialogModelByCodeQuery.graphql';
import { AIStudioProductProvider } from './components/context/productContext';
import { AIStudioContent } from './components/layout/aiStudioContent';
import { ProductSideImage } from './types';

const FullscreenOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #ffffff;
    z-index: 20;
    display: flex;
    flex-direction: column;
`;

interface AIStudioDialogBaseProps {
    productId?: number;
    variantId?: number;
    sideImages?: ProductSideImage[];
    docId?: string | null;
    compositionDocId?: string | null;
    model3D?: string | Blob | null;
    model3DRootUrl?: string | null;
    autoCapture3D?: boolean;
    onClose: () => void;
}

const AIStudioDialogBase: FC<AIStudioDialogBaseProps> = ({
    productId,
    variantId,
    sideImages,
    docId,
    compositionDocId,
    model3D,
    model3DRootUrl,
    autoCapture3D,
    onClose
}) => {
    return (
        <FullscreenOverlay>
            <Suspense
                fallback={
                    <Center style={{ margin: 'auto' }}>
                        <ZakekeLoading />
                    </Center>
                }
            >
                <AIStudioProductProvider
                    productId={productId}
                    variantId={variantId}
                    sideImages={sideImages}
                    docId={docId}
                    compositionDocId={compositionDocId}
                    model3D={model3D}
                    model3DRootUrl={model3DRootUrl}
                >
                    <AIStudioContent onClose={onClose} autoCapture3D={autoCapture3D} />
                </AIStudioProductProvider>
            </Suspense>
        </FullscreenOverlay>
    );
};

interface ProductCodeResolverProps {
    productCode: string;
    variantId?: number;
    sideImages?: ProductSideImage[];
    docId?: string | null;
    compositionDocId?: string | null;
    model3D?: string | Blob | null;
    model3DRootUrl?: string | null;
    autoCapture3D?: boolean;
    onClose: () => void;
}

const ProductCodeResolver: FC<ProductCodeResolverProps> = ({
    productCode,
    variantId,
    sideImages,
    docId,
    compositionDocId,
    model3D,
    model3DRootUrl,
    autoCapture3D,
    onClose
}) => {
    const query = useLazyLoadQuery<aiStudioDialogModelByCodeQuery>(
        graphql`
            query aiStudioDialogModelByCodeQuery($productCode: String!, $salesChannelId: String!) {
                modelIDByCode(code: $productCode, salesChannelId: $salesChannelId)
            }
        `,
        {
            productCode,
            salesChannelId: toGlobalID(1, 'SalesChannel')
        }
    );

    const resolvedProductId = toLocalID(query.modelIDByCode);

    return (
        <AIStudioDialogBase
            productId={resolvedProductId ?? undefined}
            variantId={variantId}
            sideImages={sideImages}
            docId={docId}
            compositionDocId={compositionDocId}
            model3D={model3D}
            model3DRootUrl={model3DRootUrl}
            autoCapture3D={autoCapture3D}
            onClose={onClose}
        />
    );
};

export interface AIStudioDialogProps {
    productId?: number;
    productCode?: string;
    /** Variant ID to select in 3D view */
    variantId?: number;
    sideImages?: ProductSideImage[];
    /** Design document ID to load and render on the 3D preview */
    docId?: string | null;
    /** Composition document ID to load and apply attribute selections on the 3D preview */
    compositionDocId?: string | null;
    /** Serialized Babylon scene JSON received from the customizer */
    model3D?: string | Blob | null;
    /** Root URL for external assets referenced by the serialized Babylon scene */
    model3DRootUrl?: string | null;
    onClose: () => void;
}

export const AIStudioDialog: FC<AIStudioDialogProps> = ({
    productId,
    productCode,
    variantId,
    sideImages,
    docId,
    compositionDocId,
    model3D,
    model3DRootUrl,
    onClose
}) => {
    useEffect(() => {
        // Prevent background scrolling when dialog is open
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []);

    if (productCode) {
        return (
            <Suspense fallback={null}>
                <ProductCodeResolver
                    productCode={productCode}
                    variantId={variantId}
                    sideImages={sideImages}
                    docId={docId}
                    compositionDocId={compositionDocId}
                    model3D={model3D}
                    model3DRootUrl={model3DRootUrl}
                    autoCapture3D
                    onClose={onClose}
                />
            </Suspense>
        );
    }

    return (
        <AIStudioDialogBase
            productId={productId}
            variantId={variantId}
            sideImages={sideImages}
            docId={docId}
            compositionDocId={compositionDocId}
            model3D={model3D}
            model3DRootUrl={model3DRootUrl}
            onClose={onClose}
        />
    );
};

export default AIStudioDialog;
